/**
 * ImageMetricsAdmin — /admin/image-metrics
 *
 * Métricas de engagement con fotos (tabla `image_views`): países, cantidad de
 * vistas, tiempo de visualización (dwell), aperturas, top de fotos, plantillas,
 * dispositivos y serie diaria. Consume GET /api/admin/image-metrics?days=N.
 *
 * Acceso: super_admin (ver ProtectedAdminRoute en App.tsx).
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { Clock, Eye, Globe2, Images, Loader2, MousePointerClick, RefreshCw, Users } from "lucide-react";

import { AdminPageHeader } from "@/components/admin/AdminPageHeader";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useAdminAuth } from "@/hooks/useAdminAuth";
import { useToast } from "@/hooks/use-toast";
import { WORLD_COUNTRIES } from "@/lib/worldCountries";

// ── Tipos de la respuesta del API ───────────────────────────────────────────
interface Overview {
  total_views: number;
  total_sessions: number;
  total_opens: number;
  total_dwell_ms: number;
  avg_dwell_ms: number;
  distinct_photos: number;
  distinct_countries: number;
}
interface CountryRow { country: string; views: number; sessions: number; total_dwell_ms: number; avg_dwell_ms: number }
interface PhotoRow { public_id: string; photo_title: string | null; template: string | null; views: number; opens: number; total_dwell_ms: number; avg_dwell_ms: number }
interface TemplateRow { template: string; views: number; sessions: number; total_dwell_ms: number }
interface DeviceRow { device: string; views: number; total_dwell_ms: number }
interface DayRow { day: string; views: number; sessions: number; total_dwell_ms: number }
interface ArProvinceRow { province: string; views: number; sessions: number; total_dwell_ms: number; avg_dwell_ms: number }
interface ArCityRow { city: string; province: string | null; views: number; sessions: number; total_dwell_ms: number; avg_dwell_ms: number }
interface MetricsResponse {
  days: number;
  overview: Overview;
  by_country: CountryRow[];
  by_photo: PhotoRow[];
  by_template: TemplateRow[];
  by_device: DeviceRow[];
  by_day: DayRow[];
  ar_by_province: ArProvinceRow[];
  ar_by_city: ArCityRow[];
  note?: string;
}

// ── Helpers ──────────────────────────────────────────────────────────────────
const COUNTRY_NAME = new Map(WORLD_COUNTRIES.map((c) => [c.code, c.name]));

function fmtDwell(ms: number): string {
  const s = Math.max(0, Math.round(ms / 1000));
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  const r = s % 60;
  if (m < 60) return r ? `${m}m ${r}s` : `${m}m`;
  const h = Math.floor(m / 60);
  const rm = m % 60;
  return rm ? `${h}h ${rm}m` : `${h}h`;
}
function flag(code: string): string {
  if (!/^[A-Za-z]{2}$/.test(code)) return "🏳️";
  const A = 0x1f1e6;
  return String.fromCodePoint(A + code.toUpperCase().charCodeAt(0) - 65, A + code.toUpperCase().charCodeAt(1) - 65);
}
function countryLabel(code: string): string {
  if (code === "??") return "Desconocido";
  return COUNTRY_NAME.get(code.toUpperCase()) ?? code;
}
function nfmt(n: number): string {
  return n.toLocaleString("es-AR");
}

const DAY_OPTIONS = [
  { value: "7", label: "Últimos 7 días" },
  { value: "30", label: "Últimos 30 días" },
  { value: "90", label: "Últimos 90 días" },
  { value: "365", label: "Último año" },
];

function Scorecard({ icon: Icon, label, value, sub }: { icon: typeof Eye; label: string; value: string; sub?: string }) {
  return (
    <Card>
      <CardContent className="flex items-center gap-3 p-4">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-muted">
          <Icon className="h-5 w-5 text-muted-foreground" />
        </div>
        <div className="min-w-0">
          <div className="text-xs uppercase tracking-wide text-muted-foreground">{label}</div>
          <div className="truncate text-xl font-bold">{value}</div>
          {sub && <div className="truncate text-xs text-muted-foreground">{sub}</div>}
        </div>
      </CardContent>
    </Card>
  );
}

export default function ImageMetricsAdmin() {
  const { getAuthHeaders, isLoaded, isSignedIn } = useAdminAuth();
  const { toast } = useToast();
  const [days, setDays] = useState("30");
  const [data, setData] = useState<MetricsResponse | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!isLoaded || !isSignedIn) return;
    setLoading(true);
    try {
      const headers = await getAuthHeaders();
      const res = await fetch(`/api/admin/image-metrics?days=${encodeURIComponent(days)}`, { headers });
      if (!res.ok) {
        const e = await res.json().catch(() => ({}));
        throw new Error(e.error || `Error ${res.status}`);
      }
      setData((await res.json()) as MetricsResponse);
    } catch (err) {
      toast({
        title: "No se pudieron cargar las métricas",
        description: err instanceof Error ? err.message : "Error desconocido",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  }, [days, getAuthHeaders, isLoaded, isSignedIn, toast]);

  useEffect(() => {
    void load();
  }, [load]);

  const ov = data?.overview;
  const dayChart = useMemo(
    () => (data?.by_day ?? []).map((d) => ({ ...d, dwell_min: Math.round(d.total_dwell_ms / 60000) })),
    [data],
  );
  const countryChart = useMemo(
    () =>
      (data?.by_country ?? []).slice(0, 12).map((c) => ({
        name: `${flag(c.country)} ${c.country === "??" ? "??" : c.country}`,
        views: c.views,
      })),
    [data],
  );
  const provinceChart = useMemo(
    () => (data?.ar_by_province ?? []).slice(0, 12).map((p) => ({ name: p.province, views: p.views })),
    [data],
  );

  return (
    <div className="space-y-6 p-1">
      <AdminPageHeader description="Dwell, países y top de fotos (image_views). Subir/editar → Portfolio." />

      <div className="flex flex-wrap items-center gap-3">
        <Select value={days} onValueChange={setDays}>
          <SelectTrigger className="w-[180px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {DAY_OPTIONS.map((o) => (
              <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button variant="outline" size="sm" onClick={() => void load()} disabled={loading}>
          {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-2 h-4 w-4" />}
          Actualizar
        </Button>
        {data?.note && <span className="text-xs text-muted-foreground">{data.note}</span>}
      </div>

      {/* Scorecards */}
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4 xl:grid-cols-7">
        <Scorecard icon={Eye} label="Vistas" value={nfmt(ov?.total_views ?? 0)} />
        <Scorecard icon={Users} label="Sesiones" value={nfmt(ov?.total_sessions ?? 0)} />
        <Scorecard icon={MousePointerClick} label="Aperturas" value={nfmt(ov?.total_opens ?? 0)} />
        <Scorecard icon={Clock} label="Tiempo total" value={fmtDwell(ov?.total_dwell_ms ?? 0)} />
        <Scorecard icon={Clock} label="Dwell promedio" value={fmtDwell(ov?.avg_dwell_ms ?? 0)} sub="por vista" />
        <Scorecard icon={Images} label="Fotos vistas" value={nfmt(ov?.distinct_photos ?? 0)} />
        <Scorecard icon={Globe2} label="Países" value={nfmt(ov?.distinct_countries ?? 0)} />
      </div>

      {/* ════ Argentina — provincia / ciudad (lo más importante) ════ */}
      <Card className="border-sky-500/40">
        <CardHeader>
          <CardTitle className="text-base">🇦🇷 Argentina — provincias y ciudades</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 lg:grid-cols-2">
            {/* Chart provincias */}
            <div className="h-[300px] w-full">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={provinceChart} layout="vertical" margin={{ left: 8, right: 8, top: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-muted" horizontal={false} />
                  <XAxis type="number" tick={{ fontSize: 11 }} allowDecimals={false} />
                  <YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} width={120} />
                  <Tooltip formatter={(v: number) => [nfmt(v), "Vistas"]} />
                  <Bar dataKey="views" fill="#0ea5e9" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
            {/* Tabla provincias */}
            <div className="max-h-[300px] overflow-auto">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-background">
                  <tr className="border-b text-left text-xs text-muted-foreground">
                    <th className="px-3 py-2">Provincia</th>
                    <th className="px-3 py-2 text-right">Vistas</th>
                    <th className="px-3 py-2 text-right">Sesiones</th>
                    <th className="px-3 py-2 text-right">Tiempo</th>
                    <th className="px-3 py-2 text-right">Dwell prom.</th>
                  </tr>
                </thead>
                <tbody>
                  {(data?.ar_by_province ?? []).map((p) => (
                    <tr key={p.province} className="border-b last:border-0">
                      <td className="px-3 py-2">{p.province}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{nfmt(p.views)}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{nfmt(p.sessions)}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{fmtDwell(p.total_dwell_ms)}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{fmtDwell(p.avg_dwell_ms)}</td>
                    </tr>
                  ))}
                  {(data?.ar_by_province?.length ?? 0) === 0 && (
                    <tr><td colSpan={5} className="px-3 py-6 text-center text-muted-foreground">Sin datos de Argentina en el período.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Tabla ciudades */}
          <div>
            <div className="mb-1 px-1 text-xs font-medium uppercase tracking-wide text-muted-foreground">Ciudades</div>
            <div className="max-h-[360px] overflow-auto rounded-md border">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-background">
                  <tr className="border-b text-left text-xs text-muted-foreground">
                    <th className="px-3 py-2">Ciudad</th>
                    <th className="px-3 py-2">Provincia</th>
                    <th className="px-3 py-2 text-right">Vistas</th>
                    <th className="px-3 py-2 text-right">Sesiones</th>
                    <th className="px-3 py-2 text-right">Tiempo</th>
                    <th className="px-3 py-2 text-right">Dwell prom.</th>
                  </tr>
                </thead>
                <tbody>
                  {(data?.ar_by_city ?? []).map((c, i) => (
                    <tr key={`${c.city}-${c.province ?? ""}-${i}`} className="border-b last:border-0">
                      <td className="px-3 py-2">{c.city}</td>
                      <td className="px-3 py-2 text-muted-foreground">{c.province || "—"}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{nfmt(c.views)}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{nfmt(c.sessions)}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{fmtDwell(c.total_dwell_ms)}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{fmtDwell(c.avg_dwell_ms)}</td>
                    </tr>
                  ))}
                  {(data?.ar_by_city?.length ?? 0) === 0 && (
                    <tr><td colSpan={6} className="px-3 py-6 text-center text-muted-foreground">Sin datos de ciudades en el período.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Serie diaria */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Actividad diaria</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-[260px] w-full">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={dayChart} margin={{ left: 0, right: 8, top: 8, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                <XAxis dataKey="day" tick={{ fontSize: 11 }} minTickGap={24} />
                <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                <Tooltip
                  formatter={(value: number, name: string) =>
                    name === "dwell_min" ? [`${value} min`, "Tiempo"] : [nfmt(value), name === "views" ? "Vistas" : "Sesiones"]
                  }
                />
                <Line type="monotone" dataKey="views" stroke="#6366f1" strokeWidth={2} dot={false} />
                <Line type="monotone" dataKey="sessions" stroke="#10b981" strokeWidth={2} dot={false} />
                <Line type="monotone" dataKey="dwell_min" stroke="#f59e0b" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
          <div className="mt-2 flex gap-4 text-xs text-muted-foreground">
            <span><span className="inline-block h-2 w-2 rounded-full" style={{ background: "#6366f1" }} /> Vistas</span>
            <span><span className="inline-block h-2 w-2 rounded-full" style={{ background: "#10b981" }} /> Sesiones</span>
            <span><span className="inline-block h-2 w-2 rounded-full" style={{ background: "#f59e0b" }} /> Minutos vistos</span>
          </div>
        </CardContent>
      </Card>

      {/* Países: chart + tabla */}
      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader><CardTitle className="text-base">Vistas por país (top 12)</CardTitle></CardHeader>
          <CardContent>
            <div className="h-[300px] w-full">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={countryChart} layout="vertical" margin={{ left: 8, right: 8, top: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-muted" horizontal={false} />
                  <XAxis type="number" tick={{ fontSize: 11 }} allowDecimals={false} />
                  <YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} width={70} />
                  <Tooltip formatter={(v: number) => [nfmt(v), "Vistas"]} />
                  <Bar dataKey="views" fill="#6366f1" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle className="text-base">Detalle por país</CardTitle></CardHeader>
          <CardContent className="p-0">
            <div className="max-h-[320px] overflow-auto">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-background">
                  <tr className="border-b text-left text-xs text-muted-foreground">
                    <th className="px-3 py-2">País</th>
                    <th className="px-3 py-2 text-right">Vistas</th>
                    <th className="px-3 py-2 text-right">Sesiones</th>
                    <th className="px-3 py-2 text-right">Tiempo</th>
                    <th className="px-3 py-2 text-right">Dwell prom.</th>
                  </tr>
                </thead>
                <tbody>
                  {(data?.by_country ?? []).map((c) => (
                    <tr key={c.country} className="border-b last:border-0">
                      <td className="px-3 py-2">{flag(c.country)} {countryLabel(c.country)} <span className="text-muted-foreground">({c.country})</span></td>
                      <td className="px-3 py-2 text-right tabular-nums">{nfmt(c.views)}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{nfmt(c.sessions)}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{fmtDwell(c.total_dwell_ms)}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{fmtDwell(c.avg_dwell_ms)}</td>
                    </tr>
                  ))}
                  {(data?.by_country?.length ?? 0) === 0 && (
                    <tr><td colSpan={5} className="px-3 py-6 text-center text-muted-foreground">Sin datos en el período.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Top fotos */}
      <Card>
        <CardHeader><CardTitle className="text-base">Top fotos por tiempo de visualización</CardTitle></CardHeader>
        <CardContent className="p-0">
          <div className="max-h-[420px] overflow-auto">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-background">
                <tr className="border-b text-left text-xs text-muted-foreground">
                  <th className="px-3 py-2">Foto</th>
                  <th className="px-3 py-2">Plantilla</th>
                  <th className="px-3 py-2 text-right">Vistas</th>
                  <th className="px-3 py-2 text-right">Aperturas</th>
                  <th className="px-3 py-2 text-right">Tiempo total</th>
                  <th className="px-3 py-2 text-right">Dwell prom.</th>
                </tr>
              </thead>
              <tbody>
                {(data?.by_photo ?? []).map((p) => (
                  <tr key={p.public_id} className="border-b last:border-0">
                    <td className="px-3 py-2 max-w-[280px] truncate" title={p.public_id}>
                      {p.photo_title || p.public_id}
                    </td>
                    <td className="px-3 py-2 text-muted-foreground">{p.template ?? "—"}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{nfmt(p.views)}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{nfmt(p.opens)}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{fmtDwell(p.total_dwell_ms)}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{fmtDwell(p.avg_dwell_ms)}</td>
                  </tr>
                ))}
                {(data?.by_photo?.length ?? 0) === 0 && (
                  <tr><td colSpan={6} className="px-3 py-6 text-center text-muted-foreground">Sin datos en el período.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Plantillas + Dispositivos */}
      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader><CardTitle className="text-base">Por plantilla</CardTitle></CardHeader>
          <CardContent className="p-0">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left text-xs text-muted-foreground">
                  <th className="px-3 py-2">Plantilla</th>
                  <th className="px-3 py-2 text-right">Vistas</th>
                  <th className="px-3 py-2 text-right">Sesiones</th>
                  <th className="px-3 py-2 text-right">Tiempo</th>
                </tr>
              </thead>
              <tbody>
                {(data?.by_template ?? []).map((t) => (
                  <tr key={t.template} className="border-b last:border-0">
                    <td className="px-3 py-2">{t.template}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{nfmt(t.views)}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{nfmt(t.sessions)}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{fmtDwell(t.total_dwell_ms)}</td>
                  </tr>
                ))}
                {(data?.by_template?.length ?? 0) === 0 && (
                  <tr><td colSpan={4} className="px-3 py-6 text-center text-muted-foreground">Sin datos.</td></tr>
                )}
              </tbody>
            </table>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle className="text-base">Por dispositivo</CardTitle></CardHeader>
          <CardContent className="p-0">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left text-xs text-muted-foreground">
                  <th className="px-3 py-2">Dispositivo</th>
                  <th className="px-3 py-2 text-right">Vistas</th>
                  <th className="px-3 py-2 text-right">Tiempo</th>
                </tr>
              </thead>
              <tbody>
                {(data?.by_device ?? []).map((d) => (
                  <tr key={d.device} className="border-b last:border-0">
                    <td className="px-3 py-2">{d.device}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{nfmt(d.views)}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{fmtDwell(d.total_dwell_ms)}</td>
                  </tr>
                ))}
                {(data?.by_device?.length ?? 0) === 0 && (
                  <tr><td colSpan={3} className="px-3 py-6 text-center text-muted-foreground">Sin datos.</td></tr>
                )}
              </tbody>
            </table>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
